import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { WishlistEntry } from '../types/social';

interface Result {
  wishlist: WishlistEntry[];
  byCode: Record<string, WishlistEntry>;
  loading: boolean;
  add: (code: string) => Promise<{ ok: boolean; reason?: 'limit_reached' }>;
  remove: (code: string) => Promise<void>;
  reorder: (codes: string[]) => Promise<void>;
  refresh: () => Promise<void>;
}

const MAX_PRIORITY = 5;

export function useWishlist(userId: string): Result {
  const [wishlist, setWishlist] = useState<WishlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setWishlist([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('wishlist')
      .select('user_id, sticker_code, priority, created_at')
      .eq('user_id', userId)
      .order('priority', { ascending: true });
    if (error) {
      console.error('useWishlist load', error);
      setWishlist([]);
    } else {
      setWishlist((data ?? []) as WishlistEntry[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = useCallback(
    async (code: string): Promise<{ ok: boolean; reason?: 'limit_reached' }> => {
      if (wishlist.some((w) => w.sticker_code === code)) return { ok: true };
      if (wishlist.length >= MAX_PRIORITY) return { ok: false, reason: 'limit_reached' };
      const usedPriorities = new Set(wishlist.map((w) => w.priority));
      let nextPriority = 1;
      while (usedPriorities.has(nextPriority) && nextPriority <= MAX_PRIORITY) nextPriority++;
      const { data, error } = await supabase
        .from('wishlist')
        .insert({ user_id: userId, sticker_code: code, priority: nextPriority })
        .select('user_id, sticker_code, priority, created_at')
        .single();
      if (error || !data) {
        console.error('useWishlist add', error);
        return { ok: false };
      }
      setWishlist((prev) => [...prev, data as WishlistEntry].sort((a, b) => a.priority - b.priority));
      return { ok: true };
    },
    [userId, wishlist]
  );

  const remove = useCallback(
    async (code: string) => {
      const { error } = await supabase
        .from('wishlist')
        .delete()
        .eq('user_id', userId)
        .eq('sticker_code', code);
      if (error) {
        console.error('useWishlist remove', error);
        return;
      }
      setWishlist((prev) => prev.filter((w) => w.sticker_code !== code));
    },
    [userId]
  );

  const reorder = useCallback(
    async (codesInOrder: string[]) => {
      // Compacta priorities pra evitar conflito com unique (user_id, priority)
      // Estratégia: mover tudo pro range 100..104 (limpa), depois mover pra 1..N final.
      // Como o check constraint é 1..5, preciso evitar > 5. Workaround: priorities NEGATIVAS
      // não permitidas pelo CHECK. Solução: faz update em sequência de 1 priority extra.
      // Truque: deletar + reinserir. Mais simples e garantido.
      const tempByCode = new Map(wishlist.map((w) => [w.sticker_code, w]));
      const newOrder = codesInOrder
        .map((code, idx) => {
          const entry = tempByCode.get(code);
          if (!entry) return null;
          return { ...entry, priority: idx + 1 };
        })
        .filter((x): x is WishlistEntry => x !== null);

      if (newOrder.length === 0) return;

      // Deletar todos, depois reinserir (mesma transação não dá pra fazer em 1 round-trip
      // sem RPC; aceitamos pequena janela de inconsistência)
      const { error: delErr } = await supabase
        .from('wishlist')
        .delete()
        .eq('user_id', userId);
      if (delErr) {
        console.error('useWishlist reorder delete', delErr);
        return;
      }
      const { error: insErr } = await supabase
        .from('wishlist')
        .insert(
          newOrder.map((w) => ({ user_id: userId, sticker_code: w.sticker_code, priority: w.priority }))
        );
      if (insErr) {
        console.error('useWishlist reorder insert', insErr);
      }
      // Optimistic state já mostra na nova ordem; sincroniza quando completa.
      setWishlist(newOrder);
    },
    [userId, wishlist]
  );

  const byCode: Record<string, WishlistEntry> = {};
  for (const w of wishlist) byCode[w.sticker_code] = w;

  return { wishlist, byCode, loading, add, remove, reorder, refresh: load };
}
